// =============================================================================
// MobileNav — bottom nav + drawer for phones under 720px.
//
// The sidebar collapses on mobile (it would eat the whole screen). This
// component replaces it: a sticky bottom bar with four tabs (AI, Teams,
// DMs, Units) that opens a slide-up drawer with the relevant rows.
//
// Tapping a row in the drawer selects that scope and closes the drawer,
// returning the user to the thread view full-bleed.
// =============================================================================
import React, { useState } from "react";
import { SevricMark } from "./SevricMark.jsx";

const TEAM_ICON = {
  people: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  wrench: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  ),
  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  sun: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
};

const ACCENT = {
  cobalt:   { bg: "rgba(46,86,148,0.10)", fg: "#2E5694" },
  amber:    { bg: "rgba(176,94,19,0.10)", fg: "#B05E13" },
  green:    { bg: "rgba(42,109,56,0.10)", fg: "#2A6D38" },
  gold:     { bg: "rgba(148,106,18,0.12)", fg: "#946A12" },
  burgundy: { bg: "rgba(124,41,53,0.10)", fg: "#7C2935" },
};

export function MobileNav({ profile, channels, dms, entityThreads, unread, onSelect, onOpenAi, onOpenCompose, onOpenArchive }) {
  const [drawerKind, setDrawerKind] = useState(null);

  const counts = {
    teams: channels?.length || 0,
    dms:   (dms || []).filter((d) => !d.is_ai).length,
    units: entityThreads?.length || 0,
  };
  const anyUnread = (kind) => Object.entries(unread || {}).some(([k, v]) => k.startsWith(kind + ":") && v);

  return (
    <>
      <nav className="chat-mobile-nav">
        <button type="button" className="chat-mob-btn" onClick={onOpenAi}>
          <span className="chat-mob-ico ai-slot"><SevricMark size={20} /></span>
          <span className="chat-mob-label">SEVRIC AI</span>
        </button>
        <button type="button" className="chat-mob-btn" onClick={() => setDrawerKind("teams")}>
          <span className="chat-mob-ico">{TEAM_ICON.people}</span>
          <span className="chat-mob-label">Teams</span>
          {anyUnread("channel") && <span className="chat-mob-dot" />}
        </button>
        <button type="button" className="chat-mob-btn" onClick={() => setDrawerKind("dms")}>
          <span className="chat-mob-ico">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
          </span>
          <span className="chat-mob-label">DMs</span>
          {anyUnread("dm") && <span className="chat-mob-dot" />}
        </button>
        <button type="button" className="chat-mob-btn" onClick={() => setDrawerKind("units")}>
          <span className="chat-mob-ico">
            <svg viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="14">
              <path d="M 4 16 L 8 9 L 14 7 L 30 7 L 34 9 L 36 14 L 38 16 L 6 16 Z" fill="currentColor" opacity="0.18" />
              <circle cx="12" cy="16" r="2" /><circle cx="30" cy="16" r="2" />
            </svg>
          </span>
          <span className="chat-mob-label">Units</span>
          {anyUnread("entity_thread") && <span className="chat-mob-dot" />}
        </button>
        <button type="button" className="chat-mob-btn" onClick={onOpenCompose}>
          <span className="chat-mob-ico chat-mob-plus">+</span>
          <span className="chat-mob-label">New</span>
        </button>
      </nav>

      {drawerKind && (
        <div className="chat-mob-drawer-bg" onClick={() => setDrawerKind(null)}>
          <div className="chat-mob-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="chat-mob-drawer-handle" />
            <div className="chat-sb-section-h cv" style={{ padding: "0 18px 8px" }}>
              <span>{drawerKind === "teams" ? "Teams" : drawerKind === "dms" ? "Direct Messages" : "Unit threads"}</span>
              <span className="count" onClick={() => { setDrawerKind(null); onOpenCompose?.(); }} style={{ cursor: "pointer" }}>+ new</span>
            </div>
            <div style={{ overflowY: "auto", maxHeight: "62vh" }}>
              {drawerKind === "teams" && channels.map((ch) => {
                const accent = ACCENT[ch.accent] || ACCENT.cobalt;
                const icon = TEAM_ICON[ch.icon] || TEAM_ICON.people;
                const u = unread?.[`channel:${ch.id}`];
                return (
                  <div key={ch.id} className="chat-sb-item" onClick={() => { onSelect({ kind: "channel", id: ch.id }); setDrawerKind(null); }}>
                    <div className="chat-sb-avi unit" style={{ background: accent.bg, color: accent.fg }}>{icon}</div>
                    <div className="chat-sb-text">
                      <div className="chat-sb-name">{ch.name}</div>
                      <div className="chat-sb-sub">{ch.description || ""}</div>
                    </div>
                    {u && <span className="chat-sb-unread-dot" />}
                  </div>
                );
              })}
              {drawerKind === "dms" && (dms || []).filter((d) => !d.is_ai).map((dm) => {
                const other = dm.participant_a === profile.uid ? dm.b : dm.a;
                const name = other?.display_name || other?.uid || "Someone";
                const initials = (other?.initials || name.slice(0, 2)).toUpperCase();
                const u = unread?.[`dm:${dm.id}`];
                return (
                  <div key={dm.id} className="chat-sb-item" onClick={() => { onSelect({ kind: "dm", id: dm.id }); setDrawerKind(null); }}>
                    <div className="chat-sb-avi h2">{initials}<span className="presence" /></div>
                    <div className="chat-sb-text">
                      <div className="chat-sb-name">{name}</div>
                      <div className="chat-sb-sub">{other?.presence_label || "DM"}</div>
                    </div>
                    {u && <span className="chat-sb-unread-dot" />}
                  </div>
                );
              })}
              {drawerKind === "units" && (entityThreads || []).map((et) => {
                const u = unread?.[`entity_thread:${et.id}`];
                return (
                  <div key={et.id} className="chat-sb-item" onClick={() => { onSelect({ kind: "entity_thread", id: et.id }); setDrawerKind(null); }}>
                    <div className="chat-sb-avi unit">
                      <svg viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="14">
                        <path d="M 4 16 L 8 9 L 14 7 L 30 7 L 34 9 L 36 14 L 38 16 L 6 16 Z" fill="currentColor" opacity="0.18" />
                        <circle cx="12" cy="16" r="2" /><circle cx="30" cy="16" r="2" />
                      </svg>
                    </div>
                    <div className="chat-sb-text">
                      <div className="chat-sb-name">{et.entity_label || et.entity_id}</div>
                      <div className="chat-sb-sub">{et.entity_kind.replace("_", " ")}</div>
                    </div>
                    {u && <span className="chat-sb-unread-dot" />}
                  </div>
                );
              })}
              {drawerKind === "units" && (
                <div className="chat-sb-archive" onClick={() => { setDrawerKind(null); onOpenArchive?.(); }} style={{ marginTop: 10 }}>
                  <span>📦 Past units · archived</span>
                  <span style={{ color: "var(--accent, #2E5694)" }}>→</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
